import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import {
  addOutboundAllowlist,
  confirmOutbound,
  listOutboundAllowlist,
  listOutboundMessages,
  listTenants,
  prepareOutbound,
  type OutboundAllowlistEntry,
  type OutboundConfirmResult,
  type OutboundDraft,
  type OutboundMessage,
} from "../lib/api";

type TenantOption = { tenant_id: string; bot_name?: string };
type StatusKind = "sent" | "failed" | "unknown";

const STATUS_STYLE: Record<StatusKind, string> = {
  sent: "border-emerald-500/30 bg-emerald-500/15 text-emerald-300",
  failed: "border-rose-500/30 bg-rose-500/15 text-rose-300",
  unknown: "border-amber-500/30 bg-amber-500/15 text-amber-300",
};

function statusKind(status?: string | null): StatusKind {
  const normalized = status?.toLowerCase();
  if (normalized === "sent" || normalized === "success" || normalized === "delivered") return "sent";
  if (normalized === "failed" || normalized === "error" || normalized === "rejected") return "failed";
  return "unknown";
}

function StatusBadge({ status }: { status?: string | null }) {
  const kind = statusKind(status);
  const label = kind === "sent" ? "accepted" : kind;
  return <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-bold ${STATUS_STYLE[kind]}`}>{label}</span>;
}

function timestamp(value?: string | number | null): number | null {
  if (value === null || value === undefined || value === "") return null;
  const numeric = typeof value === "number" ? value : /^\d+(\.\d+)?$/.test(value) ? Number(value) : null;
  if (numeric !== null) return numeric < 1e12 ? numeric * 1000 : numeric;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

function formatTime(value?: string | number | null): string {
  const ms = timestamp(value);
  return ms === null ? "—" : new Date(ms).toLocaleString("zh-HK", { hour12: false });
}

function isExpired(entry: OutboundAllowlistEntry): boolean {
  const expires = timestamp(entry.expires_at);
  return expires !== null && expires <= Date.now();
}

export default function OutboundPage() {
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [allowlist, setAllowlist] = useState<OutboundAllowlistEntry[]>([]);
  const [directoryLoading, setDirectoryLoading] = useState(true);
  const [directoryError, setDirectoryError] = useState("");

  const [tenantId, setTenantId] = useState("");
  const [contactId, setContactId] = useState("");
  const [newContactId, setNewContactId] = useState("");
  const [note, setNote] = useState("");
  const [requestedBy, setRequestedBy] = useState("");
  const [approvedBy, setApprovedBy] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [templateVersion, setTemplateVersion] = useState("");
  const [body, setBody] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState("");

  const [draft, setDraft] = useState<OutboundDraft | null>(null);
  const [confirmResult, setConfirmResult] = useState<OutboundConfirmResult | null>(null);
  const [adding, setAdding] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");

  const [messages, setMessages] = useState<OutboundMessage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const refreshAllowlist = useCallback(async () => {
    const data = await listOutboundAllowlist();
    setAllowlist(Array.isArray(data.entries) ? data.entries : []);
  }, []);

  const loadDirectory = useCallback(async () => {
    setDirectoryLoading(true);
    setDirectoryError("");
    try {
      const [tenantData, allowlistData] = await Promise.all([listTenants(), listOutboundAllowlist()]);
      if (tenantData.success === false) throw new Error(tenantData.error || "租戶清單載入失敗");
      setTenants(Array.isArray(tenantData.tenants) ? tenantData.tenants : []);
      setAllowlist(Array.isArray(allowlistData.entries) ? allowlistData.entries : []);
    } catch (err) {
      setDirectoryError(err instanceof Error ? err.message : "清單載入失敗");
    } finally {
      setDirectoryLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDirectory();
  }, [loadDirectory]);

  const loadMessages = useCallback(async () => {
    if (!tenantId) {
      setMessages([]);
      setHistoryError("");
      return;
    }
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const data = await listOutboundMessages(tenantId);
      setMessages(Array.isArray(data.messages) ? data.messages : []);
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : "歷史載入失敗");
    } finally {
      setHistoryLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    setDraft(null);
    setConfirmResult(null);
    setNotice("");
    setActionError("");
  }, [tenantId, contactId, templateId, templateVersion, body, idempotencyKey]);

  const tenantEntries = useMemo(
    () => allowlist.filter((entry) => !entry.tenant_id || entry.tenant_id === tenantId),
    [allowlist, tenantId]
  );
  const activeEntries = useMemo(() => tenantEntries.filter((entry) => !isExpired(entry)), [tenantEntries]);
  const selectedEntry = activeEntries.find((entry) => entry.contact_id === contactId) ?? null;
  const expiredCount = tenantEntries.length - activeEntries.length;

  async function handleAddAllowlist(event: FormEvent) {
    event.preventDefault();
    if (!tenantId || !newContactId.trim() || !approvedBy.trim()) return;
    setAdding(true);
    setNotice("");
    setActionError("");
    try {
      const contact = newContactId.trim();
      await addOutboundAllowlist({
        tenantId,
        contactId: contact,
        approvedBy: approvedBy.trim(),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      await refreshAllowlist();
      setContactId(contact);
      setNewContactId("");
      setNote("");
      setNotice(`已將 ${contact} 加入 allowlist，並選為本次收件聯絡人。`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "加入 allowlist 失敗");
    } finally {
      setAdding(false);
    }
  }

  async function handlePrepare(event: FormEvent) {
    event.preventDefault();
    if (!tenantId || !contactId || !selectedEntry || !body.trim() || !idempotencyKey.trim() || !requestedBy.trim()) return;
    setPreparing(true);
    setDraft(null);
    setConfirmResult(null);
    setNotice("");
    setActionError("");
    try {
      const result = await prepareOutbound({
        tenantId,
        contactId,
        templateId: templateId.trim(),
        templateVersion: templateVersion.trim(),
        body,
        idempotencyKey: idempotencyKey.trim(),
        requestedBy: requestedBy.trim(),
      });
      setDraft(result.draft);
      setNotice("草稿已準備，尚未發送。請核對預覽，再確認發送。");
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Prepare 失敗");
    } finally {
      setPreparing(false);
    }
  }

  async function handleConfirm() {
    if (!draft || confirmResult || !approvedBy.trim()) return;
    setConfirming(true);
    setNotice("");
    setActionError("");
    try {
      const result = await confirmOutbound({
        draftId: draft.id,
        bodyHash: draft.body_hash,
        approvedBy: approvedBy.trim(),
      });
      setConfirmResult(result);
      await loadMessages();
    } catch (err) {
      setConfirmResult({
        id: draft.id,
        status: "unknown",
        provider_ref: null,
        error: err instanceof Error ? err.message : "確認請求網絡異常",
      });
      setActionError("發送結果未明。請先重新載入歷史核對，切勿直接重複發送。");
      await loadMessages();
    } finally {
      setConfirming(false);
    }
  }

  const canPrepare = Boolean(
    tenantId && contactId && selectedEntry && body.trim() && idempotencyKey.trim() && requestedBy.trim()
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-100">📤 主動發送</h1>
        <p className="text-sm text-slate-400 mt-1 leading-relaxed">
          只可向 allowlist 內的聯絡人發送。流程必須先 prepare 預覽，再由核准人 confirm；系統不提供自由輸入電話的繞道路徑。
        </p>
      </div>

      <div className="rounded-xl border border-sky-500/25 bg-sky-500/10 p-4 text-xs text-sky-200 leading-relaxed space-y-1">
        <p>Prepare 只建立草稿，不會發送。Confirm 會核對後端核發的 body hash；任何草稿欄位變更都會作廢目前預覽。網絡異常以 unknown 處理，應先查歷史，避免重複發送。</p>
        <p><strong>accepted</strong> 表示供應商接受請求，不等同手機已送達。<strong>provider_ref=null</strong> 表示本次回應沒有可識別的訊息 ID；需由收件人或供應商交付回執另行核實。</p>
      </div>

      {directoryError && (
        <div role="alert" className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-300">
          {directoryError}
          <button onClick={() => void loadDirectory()} className="ml-3 underline">重新載入清單</button>
        </div>
      )}
      {notice && <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-300">{notice}</div>}
      {actionError && <div role="alert" className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-300">{actionError}</div>}

      <section className="bg-slate-900 rounded-xl border border-slate-800 p-5 md:p-6">
        <h2 className="text-lg font-semibold text-emerald-400 mb-4">1. 租戶與 allowlist 聯絡人</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label htmlFor="outbound-tenant" className="block text-xs text-slate-400 mb-1">租戶</label>
            <select
              id="outbound-tenant"
              aria-label="租戶"
              value={tenantId}
              onChange={(event) => {
                setTenantId(event.target.value);
                setContactId("");
              }}
              disabled={directoryLoading}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100 disabled:opacity-50"
            >
              <option value="">請選擇租戶</option>
              {tenants.map((tenant) => (
                <option key={tenant.tenant_id} value={tenant.tenant_id}>
                  {tenant.bot_name ? `${tenant.bot_name} · ${tenant.tenant_id}` : tenant.tenant_id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="outbound-contact" className="block text-xs text-slate-400 mb-1">Allowlist 聯絡人</label>
            <select
              id="outbound-contact"
              aria-label="Allowlist 聯絡人"
              value={contactId}
              onChange={(event) => setContactId(event.target.value)}
              disabled={!tenantId || directoryLoading}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100 disabled:opacity-50"
            >
              <option value="">請選擇已核准聯絡人</option>
              {activeEntries.map((entry) => (
                <option key={entry.contact_id} value={entry.contact_id}>
                  {entry.name} · {entry.phone} · {entry.contact_id}
                </option>
              ))}
            </select>
            {tenantId && !directoryLoading && (
              <p className="text-[11px] text-slate-500 mt-1">
                可用聯絡人 {activeEntries.length} 位{expiredCount > 0 ? `；已排除 ${expiredCount} 位過期聯絡人` : ""}。
              </p>
            )}
          </div>
        </div>

        {selectedEntry && (
          <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-xs text-slate-400 space-y-1">
            <p><span className="text-slate-500">核准人：</span>{selectedEntry.approved_by} · <span className="text-slate-500">核准時間：</span>{formatTime(selectedEntry.approved_at)}</p>
            <p><span className="text-slate-500">到期：</span>{selectedEntry.expires_at ? formatTime(selectedEntry.expires_at) : "不設到期"}</p>
            {selectedEntry.note && <p><span className="text-slate-500">備註：</span>{selectedEntry.note}</p>}
          </div>
        )}

        <form onSubmit={handleAddAllowlist} className="mt-5 pt-5 border-t border-slate-800">
          <h3 className="text-sm font-semibold text-slate-200 mb-1">加入現有聯絡人至 allowlist</h3>
          <p className="text-xs text-slate-500 mb-3">只接受通訊錄的 contact_id；這裡不是電話輸入欄，也不能藉此指定收件號碼。</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="allowlist-contact-id" className="block text-xs text-slate-400 mb-1">Contact ID（不是電話）</label>
              <input
                id="allowlist-contact-id"
                value={newContactId}
                onChange={(event) => setNewContactId(event.target.value)}
                placeholder="contact_example"
                autoComplete="off"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100"
              />
            </div>
            <div>
              <label htmlFor="outbound-approved-by" className="block text-xs text-slate-400 mb-1">Approved by</label>
              <input
                id="outbound-approved-by"
                value={approvedBy}
                onChange={(event) => setApprovedBy(event.target.value)}
                placeholder="核准人員編號或姓名"
                autoComplete="off"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="allowlist-note" className="block text-xs text-slate-400 mb-1">備註（選填）</label>
              <input
                id="allowlist-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="核准依據或用途"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={adding || !tenantId || !newContactId.trim() || !approvedBy.trim()}
            className="mt-3 px-4 py-2 rounded-lg text-sm font-medium border border-sky-500/40 bg-sky-600/20 text-sky-300 hover:bg-sky-600/30 disabled:opacity-40"
          >
            {adding ? "加入中…" : "加入 allowlist"}
          </button>
        </form>
      </section>

      <section className="bg-slate-900 rounded-xl border border-slate-800 p-5 md:p-6">
        <h2 className="text-lg font-semibold text-emerald-400 mb-4">2. 模板（選填）與 Prepare</h2>
        <form onSubmit={handlePrepare}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="outbound-template-id" className="block text-xs text-slate-400 mb-1">Template ID</label>
              <input
                id="outbound-template-id"
                value={templateId}
                onChange={(event) => setTemplateId(event.target.value)}
                autoComplete="off"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100"
              />
            </div>
            <div>
              <label htmlFor="outbound-template-version" className="block text-xs text-slate-400 mb-1">Template version</label>
              <input
                id="outbound-template-version"
                value={templateVersion}
                onChange={(event) => setTemplateVersion(event.target.value)}
                autoComplete="off"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100"
              />
            </div>
            <div>
              <label htmlFor="outbound-idempotency" className="block text-xs text-slate-400 mb-1">Idempotency key</label>
              <input
                id="outbound-idempotency"
                value={idempotencyKey}
                onChange={(event) => setIdempotencyKey(event.target.value)}
                required
                autoComplete="off"
                placeholder="每次業務操作使用唯一鍵"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100"
              />
            </div>
            <div>
              <label htmlFor="outbound-requested-by" className="block text-xs text-slate-400 mb-1">Requested by</label>
              <input
                id="outbound-requested-by"
                value={requestedBy}
                onChange={(event) => setRequestedBy(event.target.value)}
                required
                autoComplete="off"
                placeholder="操作人員編號或姓名"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="outbound-body" className="block text-xs text-slate-400 mb-1">訊息內容 Body</label>
              <textarea
                id="outbound-body"
                value={body}
                onChange={(event) => setBody(event.target.value)}
                required
                rows={5}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100 resize-y"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={preparing || !canPrepare}
            className="mt-4 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-40"
          >
            {preparing ? "Prepare 進行中…" : "Prepare 預覽（不發送）"}
          </button>
        </form>
      </section>

      {draft && (
        <section aria-label="發送預覽" className="bg-slate-900 rounded-xl border border-emerald-500/30 p-5 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-lg font-semibold text-emerald-400">3. Prepare 預覽（尚未發送）</h2>
            {confirmResult ? (
              <StatusBadge status={confirmResult.status} />
            ) : (
              <span className="inline-flex px-2 py-0.5 rounded-full border border-slate-600 bg-slate-800 text-[11px] font-bold text-slate-300">{draft.status}</span>
            )}
          </div>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            {[
              ["Draft ID", draft.id],
              ["收件號碼（由後端解析）", draft.to_phone],
              ["Template", draft.template_id ? `${draft.template_id} · ${draft.template_version || "未標版本"}` : "不使用模板"],
              ["Body hash", draft.body_hash],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-slate-800 bg-slate-950/60 p-3 min-w-0">
                <dt className="text-slate-500">{label}</dt>
                <dd className="text-slate-300 font-mono mt-1 break-all">{value}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-3">
            <div className="text-xs text-slate-500 mb-1">最終訊息內容</div>
            <pre className="rounded-lg border border-slate-800 bg-slate-950 p-4 text-sm text-slate-200 whitespace-pre-wrap break-words font-sans">{draft.body}</pre>
          </div>

          {confirmResult && (
            <div className={`mt-4 rounded-lg border p-4 text-sm ${STATUS_STYLE[statusKind(confirmResult.status)]}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">Confirm 結果</span>
                <StatusBadge status={confirmResult.status} />
              </div>
              <p className="mt-2 text-xs">ID：<span className="font-mono break-all">{confirmResult.id}</span></p>
              {confirmResult.provider_ref && <p className="mt-1 text-xs">Provider ref：<span className="font-mono break-all">{confirmResult.provider_ref}</span></p>}
              {confirmResult.error && <p className="mt-1 text-xs">Error：{confirmResult.error}</p>}
              {statusKind(confirmResult.status) === "unknown" && <p className="mt-2 text-xs font-semibold">結果未明，請先核對歷史，不要直接重發。</p>}
            </div>
          )}

          <button
            onClick={() => void handleConfirm()}
            disabled={confirming || Boolean(confirmResult) || !approvedBy.trim()}
            className="mt-4 px-4 py-2 rounded-lg text-sm font-medium bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-40"
          >
            {confirming ? "Confirm 進行中…" : confirmResult ? "已確認，請查閱結果" : "Confirm 發送"}
          </button>
          {!approvedBy.trim() && <p className="text-xs text-amber-300 mt-2">請先填寫 Approved by，才可確認發送。</p>}
        </section>
      )}

      <section className="bg-slate-900 rounded-xl border border-slate-800 p-5 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-emerald-400">發送歷史</h2>
            <p className="text-xs text-slate-500 mt-1">只顯示目前租戶的記錄；unknown 必須人工核對。</p>
          </div>
          <button
            onClick={() => void loadMessages()}
            disabled={!tenantId || historyLoading}
            className="px-3 py-2 text-sm rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
          >
            {historyLoading ? "載入中…" : "↻ 重新載入歷史"}
          </button>
        </div>

        {!tenantId ? (
          <p className="text-sm text-slate-500">請先選擇租戶。</p>
        ) : historyError ? (
          <p role="alert" className="text-sm text-rose-300">{historyError}</p>
        ) : historyLoading && messages.length === 0 ? (
          <p className="text-sm text-slate-500">歷史載入中…</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-slate-500">此租戶暫無主動發送記錄。</p>
        ) : (
          <div className="space-y-3">
            {messages.map((message) => (
              <article key={message.id} className="rounded-lg border border-slate-800 bg-slate-950/60 p-4 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <StatusBadge status={message.status} />
                  <span className="text-xs text-slate-300 font-mono break-all">{message.id}</span>
                  <span className="text-[11px] text-slate-500 ml-auto">{formatTime(message.created_at ?? message.updated_at)}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-400">
                  <p>收件號碼：<span className="text-slate-300 font-mono">{message.to_phone ?? "—"}</span></p>
                  <p>Contact ID：<span className="text-slate-300 font-mono break-all">{message.contact_id ?? "—"}</span></p>
                  <p>模板：<span className="text-slate-300 font-mono">{message.template_id ?? "—"} · {message.template_version ?? "—"}</span></p>
                  <p>Requested by：<span className="text-slate-300">{message.requested_by ?? "—"}</span></p>
                  {message.provider_ref && <p>Provider ref：<span className="text-slate-300 font-mono break-all">{message.provider_ref}</span></p>}
                  {message.idempotency_key && <p>Idempotency key：<span className="text-slate-300 font-mono break-all">{message.idempotency_key}</span></p>}
                </div>
                {message.body && <pre className="mt-3 rounded bg-slate-950 border border-slate-800 p-3 text-xs text-slate-300 whitespace-pre-wrap break-words font-sans">{message.body}</pre>}
                {message.error && <p className="mt-2 text-xs text-rose-300">Error：{message.error}</p>}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
